import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import '../../css/card.css';

import PatientTrackingFollowUpDate from './PatientTrackingFollowUpDate';
import PatientTrackingActionsList from './PatientTrackingActionsList';
import AddPatientTrackingActionModal from './AddPatientTrackingActionModal';

import { getMostRecentTrackingAction } from '../../utils/patientTrackingUtils';

import { getActionsByPatientExam } from '../../common/services/next-gen/PatientTrackingService';

/**
 * PatientTrackingSection Component
 *
 * Exam-level patient tracking section. Shows the current patient tracking
 * follow-up date, an "Add Follow Up Tracking Action" button, and the list
 * of existing actions for the exam.
 *
 * @component
 * @example
 * <PatientTrackingSection
 *   patientExamId="guid-here"
 *   readOnly={false}
 * />
 */
const PatientTrackingSection = ({ patientExamId, patientResultId, readOnly, onTrackingActionAdded, onActionsLoaded, reloadKey }) => {
const [trackingActions, setTrackingActions] = useState([]);
const [loadingActions, setLoadingActions] = useState(true);
const [showAddModal, setShowAddModal] = useState(false);
const [currentFollowUpDate, setCurrentFollowUpDate] = useState(null);
const [selectedAction, setSelectedAction] = useState(null);

/**
 * Helper function to get action ID from an action object
 * Handles multiple possible property names from the API
 */
const getActionId = (action) => {
    return action.trackingActionId
        || action.id
        || action.patientTrackingActionId
        || action.actionId;
};

    /**
     * Load tracking actions from API
     */
    const loadTrackingActions = async () => {
        if (!patientExamId) {
            setLoadingActions(false);
            return;
        }

        try {
            const actions = await getActionsByPatientExam(patientExamId);
            const mostRecent = getMostRecentTrackingAction(actions);

            setTrackingActions(actions);
            setCurrentFollowUpDate(mostRecent?.nextFollowUpDate || null);
            setLoadingActions(false);
            if (onActionsLoaded) onActionsLoaded(actions);
        } catch (error) {
            console.error('Error loading tracking actions:', error);
            setLoadingActions(false);
        }
    };

    // Load tracking actions on mount and when patientExamId changes
    useEffect(() => {
        loadTrackingActions();
    }, [patientExamId, reloadKey]);

    /**
     * Handle when a new action is added or existing action is updated
     */
    const handleActionAdded = (action, isEdit = false) => {
        let updatedActions;

        if (isEdit) {
            const updatedActionId = getActionId(action);
            updatedActions = trackingActions.map(a =>
                getActionId(a) === updatedActionId ? action : a
            );
        } else {
            updatedActions = [action, ...trackingActions];
        }

        const updatedFollowUpDate = action.nextFollowUpDate || currentFollowUpDate;

        setTrackingActions(updatedActions);
        setCurrentFollowUpDate(updatedFollowUpDate);
        setShowAddModal(false);
        setSelectedAction(null);

        if (onActionsLoaded) onActionsLoaded(updatedActions);

        if (onTrackingActionAdded) {
            onTrackingActionAdded(action, isEdit);
        }
    };

    /**
     * Open the add modal
     */
    const openAddModal = () => {
        setSelectedAction(null);
        setShowAddModal(true);
    };

    /**
     * Handle when an action row is clicked (edit mode � most-recent only)
     */
    const handleActionClick = (action) => {
        if (!readOnly) {
            setSelectedAction(action);
            setShowAddModal(true);
        }
    };

    /**
     * Close the add modal
     */
    const closeAddModal = () => {
        setShowAddModal(false);
        setSelectedAction(null);
    };

    if (!patientExamId) {
        return null;
    }

    // Determine the most recent action ID so the list can mark it as editable
    const mostRecentAction = getMostRecentTrackingAction(trackingActions);
    const mostRecentActionId = mostRecentAction ? getActionId(mostRecentAction) : null;

    return (
        <div className="patient-tracking-section mt-4">
            <Card className="">
                <Card.Header style={{ backgroundColor: 'unset' }}>
                    <span>Patient Tracking</span>
                </Card.Header>
                <Card.Body>
                    {/* Follow-Up Date Display */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <PatientTrackingFollowUpDate
                                followUpDate={currentFollowUpDate}
                                showLabel={true}
                            />
                        </div>
                        <div className="col-md-6 text-right">
                            {!readOnly && (
                                <button
                                    className="btn btn-submit"
                                    onClick={openAddModal}
                                >
                                    <i className="fas fa-plus mr-2"></i>
                                    Add Follow Up Tracking Action
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tracking Actions List */}
                    {loadingActions ? (
                        <div className="text-center py-3">
                            <i className="fas fa-spinner fa-spin"></i> Loading tracking actions...
                        </div>
                    ) : (
                        <PatientTrackingActionsList
                            actions={trackingActions}
                            showHeader={true}
                            mostRecentActionId={mostRecentActionId}
                            onActionClick={!readOnly ? handleActionClick : undefined}
                        />
                    )}

                </Card.Body>
            </Card>

            {/* Add/Edit Action Modal */}
            <AddPatientTrackingActionModal
                show={showAddModal}
                onHide={closeAddModal}
                patientExamId={patientExamId}
                patientResultId={patientResultId}
                currentFollowUpDate={currentFollowUpDate}
                onActionAdded={handleActionAdded}
                existingAction={selectedAction}
            />
        </div>
    );
};


export default PatientTrackingSection;

